import React, { useEffect, useMemo, useState } from 'react';
import { getExportPreview, exportCsv } from '../services/api';
import { Device } from '../types';

interface DataExportProps {
  devices: Device[];
}

const STANDARD_METRICS = ['temperature', 'humidity', 'pressure', 'battery'];

function toInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function formatLabel(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getMetricLabel(device: Device | undefined, key: string): string {
  const mapping = device?.parser_config?.mappings?.[key];
  if (mapping?.label) return mapping.label;
  return formatLabel(key);
}

function slugFilePart(value: string): string {
  return value
    .trim()
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 42) || 'device';
}

function formatFileDate(value: string): string {
  if (!value) return 'pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'pending';
  return date.toISOString().slice(0, 10).replace(/-/g, '');
}

export default function DataExport({ devices }: DataExportProps) {
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([...STANDARD_METRICS]);
  const [customMetrics, setCustomMetrics] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const now = new Date();
    setEnd(toInputValue(now));
    setStart(toInputValue(new Date(now.getTime() - 24 * 60 * 60 * 1000)));
  }, []);

  const isValidRange = useMemo(() => {
    if (!start || !end) return false;
    return new Date(start) < new Date(end);
  }, [start, end]);

  useEffect(() => {
    if (!selectedDeviceId || !start || !end || !isValidRange) {
      setRowCount(null);
      setCustomMetrics([]);
      return;
    }

    let cancelled = false;
    async function loadPreview() {
      setPreviewLoading(true);
      setError('');
      try {
        const data = await getExportPreview(
          selectedDeviceId,
          new Date(start).toISOString(),
          new Date(end).toISOString()
        );
        if (cancelled) return;
        setRowCount(data.rowCount);
        const custom = (data.availableMetrics || []).filter(
          (metric: string) => !STANDARD_METRICS.includes(metric) && metric !== 'time'
        );
        setCustomMetrics(custom);
        setSelectedMetrics((prev) => {
          const next = new Set(prev);
          custom.forEach((metric: string) => next.add(metric));
          return Array.from(next);
        });
      } catch (err: any) {
        if (cancelled) return;
        setError(err.response?.data?.error || 'Failed to preview export.');
        setRowCount(null);
        setCustomMetrics([]);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }

    const timer = setTimeout(loadPreview, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedDeviceId, start, end, isValidRange]);

  function toggleMetric(metric: string) {
    setSelectedMetrics((prev) =>
      prev.includes(metric) ? prev.filter((item) => item !== metric) : [...prev, metric]
    );
  }

  function selectAll() {
    setSelectedMetrics([...STANDARD_METRICS, ...customMetrics]);
  }

  function clearAll() {
    setSelectedMetrics([]);
  }

  async function handleDownload() {
    if (!selectedDeviceId || selectedMetrics.length === 0 || !isValidRange) return;
    setExportLoading(true);
    setError('');
    try {
      await exportCsv(
        selectedDeviceId,
        new Date(start).toISOString(),
        new Date(end).toISOString(),
        selectedMetrics
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to download CSV. Please try again.');
    } finally {
      setExportLoading(false);
    }
  }

  const selectedDevice = devices.find((device) => device.id === selectedDeviceId);
  const canExport = !!selectedDeviceId && selectedMetrics.length > 0 && isValidRange && !exportLoading;
  const columnCount = selectedMetrics.length + 1;
  const onlineDevices = devices.filter((device) => device.status === 'online').length;
  const exportFileName = selectedDevice
    ? `${slugFilePart(selectedDevice.name)}_${formatFileDate(start)}_${formatFileDate(end)}.csv`
    : 'Select a device to name the file';
  const exportSteps = [
    {
      key: 'device',
      label: 'Select device',
      detail: selectedDevice?.name || 'Choose the asset',
      icon: 'device' as const,
      complete: !!selectedDeviceId,
      active: !selectedDeviceId,
    },
    {
      key: 'window',
      label: 'Time window',
      detail: isValidRange ? 'Range valid' : 'Pick a valid range',
      icon: 'clock' as const,
      complete: isValidRange,
      active: !!selectedDeviceId && !isValidRange,
    },
    {
      key: 'columns',
      label: 'Metric columns',
      detail: `${selectedMetrics.length} selected`,
      icon: 'metrics' as const,
      complete: selectedMetrics.length > 0,
      active: !!selectedDeviceId && isValidRange && selectedMetrics.length === 0,
    },
  ];

  return (
    <section className="sd-page-stack sd-export-page">
      <div className="sd-page-header sd-export-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">DATA PIPELINE</div>
          <h1>Data Export</h1>
          <p>Preview telemetry volume, select fields, and download clean CSV extracts.</p>
        </div>
        <div className="sd-page-actions">
          <div className={`sd-status-pill ${canExport ? 'is-online' : 'is-warning'}`}>
            <span />
            {canExport ? 'Ready' : 'Setup needed'}
          </div>
        </div>
      </div>

      <section className="sd-export-pipeline-strip" aria-label="Export metrics">
        <ExportStat icon="device" label="Device pool" value={String(devices.length)} detail={`${onlineDevices} online`} />
        <ExportStat
          icon="database"
          label="Readings found"
          value={previewLoading ? '...' : rowCount !== null ? rowCount.toLocaleString() : '-'}
          detail={rowCount !== null && rowCount >= 50000 ? 'Capped at 50,000 rows' : 'Preview window'}
        />
        <ExportStat icon="metrics" label="Metrics" value={String(selectedMetrics.length)} detail={`${customMetrics.length} discovered`} />
        <ExportStat
          icon="package"
          label="Export state"
          value={canExport ? 'Ready' : 'Hold'}
          detail={isValidRange ? 'Range valid' : 'Check range'}
          tone={canExport ? 'success' : 'warning'}
        />
      </section>

      {error && <div className="sd-inline-alert is-danger">{error}</div>}

      <section className="sd-export-guide-workspace">
        <article className="sd-panel sd-export-guide-panel">
          <div className="sd-export-step-track" aria-label="Export setup steps">
            {exportSteps.map((item, index) => (
              <div
                className={`sd-export-step-pill ${item.active ? 'is-active' : ''} ${item.complete ? 'is-complete' : ''}`}
                key={item.key}
              >
                <span className="sd-export-step-index">{index + 1}</span>
                <ExportIcon type={item.icon} />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </span>
              </div>
            ))}
          </div>

          <div className="sd-export-guide-body">
            <section className="sd-export-step-card is-active">
              <div className="sd-export-step-card-head">
                <span className="sd-export-step-index">1</span>
                <div>
                  <h2>Select device</h2>
                  <p>Choose the asset and data pipeline for this extract.</p>
                </div>
              </div>
              <label className="sd-field">
                <span>Device</span>
                <select value={selectedDeviceId} onChange={(event) => setSelectedDeviceId(event.target.value)}>
                  <option value="">Choose a device</option>
                  {devices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.name}{device.location ? ` - ${device.location}` : ''}
                    </option>
                  ))}
                </select>
                <small>Select one asset whose readings should be exported.</small>
              </label>
              {selectedDevice && (
                <div className="sd-export-device-card">
                  <div>
                    <span>Selected Asset</span>
                    <strong>{selectedDevice.name}</strong>
                    <small>{selectedDevice.site_name || 'Unassigned site'} / {selectedDevice.area_name || 'Unassigned area'}</small>
                  </div>
                  <span className={`sd-status-label ${selectedDevice.status === 'online' ? 'is-online' : 'is-offline'}`}>
                    <i />
                    {selectedDevice.status || 'offline'}
                  </span>
                </div>
              )}
            </section>

            <section className={`sd-export-step-card ${selectedDeviceId ? 'is-active' : ''}`}>
              <div className="sd-export-step-card-head">
                <span className="sd-export-step-index">2</span>
                <div>
                  <h2>Time window</h2>
                  <p>Pick the start and end timestamps for the CSV package.</p>
                </div>
              </div>
              <div className="sd-form-grid">
                <label className="sd-field">
                  <span>From</span>
                  <input type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} />
                  <small>Start timestamp for the CSV export window.</small>
                </label>
                <label className="sd-field">
                  <span>To</span>
                  <input type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} />
                  <small>End timestamp. Must be after From.</small>
                </label>
              </div>
              {!isValidRange && start && end && (
                <div className="sd-inline-alert is-warning">The To time must be after the From time.</div>
              )}
            </section>

            <section className={`sd-export-step-card ${selectedDeviceId && isValidRange ? 'is-active' : ''}`}>
              <div className="sd-export-step-card-head">
                <span className="sd-export-step-index">3</span>
                <div>
                  <h2>Metric columns</h2>
                  <p>Select the fields to include in the CSV payload.</p>
                </div>
                <div className="sd-row-actions">
                  <button onClick={selectAll} type="button">Select All</button>
                  <button onClick={clearAll} type="button">Clear</button>
                </div>
              </div>
              <div className="sd-export-metric-grid">
                <MetricGroup
                  device={selectedDevice}
                  metrics={STANDARD_METRICS}
                  selectedMetrics={selectedMetrics}
                  title="Standard"
                  toggleMetric={toggleMetric}
                />
                <MetricGroup
                  device={selectedDevice}
                  emptyText={selectedDeviceId ? 'No custom metrics found for this device yet.' : 'Select a device to discover parser metrics.'}
                  metrics={customMetrics}
                  selectedMetrics={selectedMetrics}
                  title="Custom"
                  toggleMetric={toggleMetric}
                />
              </div>
            </section>
          </div>
        </article>

        <aside className="sd-panel sd-export-package-panel">
          <div className="sd-export-package-visual">
            <ExportIcon type="package" />
            <span className={canExport ? 'is-ready' : ''}>{canExport ? 'Ready' : 'Hold'}</span>
          </div>
          <div className="sd-export-package-copy">
            <h2>Export Package</h2>
            <p>{canExport ? 'Your CSV package is clean and ready.' : 'Complete the required setup to unlock download.'}</p>
          </div>
          <div className="sd-export-package-grid">
            <SummaryRow label="Rows" value={previewLoading ? 'Calculating...' : rowCount !== null ? rowCount.toLocaleString() : 'No preview'} />
            <SummaryRow label="Columns" value={String(columnCount)} />
            <SummaryRow label="Device" value={selectedDevice?.name || 'Not selected'} />
            <SummaryRow label="Range" value={start && end ? `${formatShortDate(start)} to ${formatShortDate(end)}` : 'Not set'} />
            <SummaryRow label="File" value={exportFileName} />
            <SummaryRow label="Metrics" value={`${selectedMetrics.length} selected`} />
          </div>
          {rowCount !== null && rowCount >= 50000 && (
            <div className="sd-inline-alert is-warning">Large exports are capped at 50,000 rows.</div>
          )}
          <button className="sd-primary-button" disabled={!canExport} onClick={handleDownload} type="button">
            <ExportIcon type="download" />
            {exportLoading ? 'Generating CSV...' : 'Download CSV'}
          </button>
          {selectedDevice && (
            <p className="sd-export-filename">
              File will be named from <strong>{selectedDevice.name}</strong> and the selected time range.
            </p>
          )}
        </aside>
      </section>
    </section>
  );
}

function ExportStat({
  detail,
  icon,
  label,
  tone,
  value,
}: {
  detail: string;
  icon: ExportIconType;
  label: string;
  tone?: 'success' | 'warning';
  value: string;
}) {
  return (
    <article className={`sd-export-stat ${tone ? `is-${tone}` : ''}`}>
      <ExportIcon type={icon} />
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

type ExportIconType = 'clock' | 'database' | 'device' | 'download' | 'metrics' | 'package';

function ExportIcon({ type }: { type: ExportIconType }) {
  return (
    <svg aria-hidden="true" className={`sd-export-icon is-${type}`} viewBox="0 0 48 48" focusable="false">
      {type === 'device' && (
        <>
          <rect x="16" y="10" width="16" height="28" rx="8" />
          <path d="M18.5 16.5h11" />
          <path d="M18.5 31.5h11" />
          <path d="M12 19.5a10 10 0 0 0 0 9" />
          <path d="M36 19.5a10 10 0 0 1 0 9" />
          <circle cx="24" cy="24" r="3" />
        </>
      )}
      {type === 'clock' && (
        <>
          <circle cx="24" cy="24" r="15" />
          <path d="M24 15v10l7 4" />
        </>
      )}
      {type === 'metrics' && (
        <>
          <rect x="9" y="25" width="7" height="12" rx="2" />
          <rect x="20.5" y="17" width="7" height="20" rx="2" />
          <rect x="32" y="11" width="7" height="26" rx="2" />
          <path d="M9 39h30" />
        </>
      )}
      {type === 'database' && (
        <>
          <ellipse cx="24" cy="14" rx="14" ry="5" />
          <path d="M10 14v20c0 2.8 6.3 5 14 5s14-2.2 14-5V14" />
          <path d="M10 24c0 2.8 6.3 5 14 5s14-2.2 14-5" />
        </>
      )}
      {type === 'package' && (
        <>
          <path d="M12 16l12-7 12 7v16L24 39 12 32z" />
          <path d="M12 16l12 7 12-7" />
          <path d="M24 23v16" />
          <path d="M18 12l12 7" />
        </>
      )}
      {type === 'download' && (
        <>
          <path d="M24 10v20" />
          <path d="M16 22l8 8 8-8" />
          <path d="M12 36h24" />
        </>
      )}
    </svg>
  );
}

function MetricGroup({
  device,
  emptyText,
  metrics,
  selectedMetrics,
  title,
  toggleMetric,
}: {
  device?: Device;
  emptyText?: string;
  metrics: string[];
  selectedMetrics: string[];
  title: string;
  toggleMetric: (metric: string) => void;
}) {
  return (
    <div className="sd-export-metric-group">
      <div className="sd-export-group-title">{title}</div>
      {metrics.length === 0 ? (
        <p>{emptyText || 'No metrics available.'}</p>
      ) : (
        metrics.map((metric) => (
          <label className="sd-export-checkbox" key={metric}>
            <input
              checked={selectedMetrics.includes(metric)}
              onChange={() => toggleMetric(metric)}
              type="checkbox"
            />
            <span>
              <strong>{getMetricLabel(device, metric)}</strong>
              <small>{metric}</small>
            </span>
          </label>
        ))
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="sd-export-summary-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatShortDate(value: string): string {
  if (!value) return '-';
  return new Date(value).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
